import { translations, type Language } from '../../translations'
import { useState } from 'react'

interface OnboardingModalProps {
    lang: Language
    onClose: () => void
    onStartTrial: () => Promise<boolean>
}

export function OnboardingModal({ lang, onClose, onStartTrial }: OnboardingModalProps) {
    const t = translations[lang]
    const isRTL = lang === 'ar'
    const [loading, setLoading] = useState(false)

    const handleStart = async () => {
        setLoading(true)
        try {
            const success = await onStartTrial()
            if (success !== false) {
                onClose()
            } else {
                window.alert('Activation failed. Please check your connection or contact support.')
            }
        } catch (error) {
            console.error(error)
            window.alert('An error occurred.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm animate-in fade-in duration-200">
            <div
                className={`w-full max-w-md rounded-2xl bg-white p-6 shadow-xl animate-in zoom-in-95 duration-200 ${isRTL ? 'rtl' : 'ltr'}`}
                dir={isRTL ? 'rtl' : 'ltr'}
            >
                {/* Welcome icon */}
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg">
                    <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                        />
                    </svg>
                </div>

                {/* Title */}
                <h2 className="mb-2 text-center text-xl font-bold text-slate-900">{t.welcomeTitle}</h2>
                <p className="mb-6 text-center text-slate-600">{t.welcomeSubtitle}</p>

                {/* Plans explanation */}
                <div className="mb-6 space-y-4">
                    {/* Trial */}
                    <div className="flex items-start gap-3 rounded-xl border border-blue-100 bg-blue-50 p-4">
                        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-white">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                        </div>
                        <div>
                            <p className="font-semibold text-blue-900">
                                {lang === 'ar' ? 'فترة التجربة' : 'Période d\'essai'}
                            </p>
                            <p className="text-sm text-blue-700">{t.trialExplanation}</p>
                        </div>
                    </div>

                    {/* Free after trial */}
                    <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-slate-400 text-white">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                />
                            </svg>
                        </div>
                        <div>
                            <p className="font-semibold text-slate-900">
                                {lang === 'ar' ? 'الخطة المجانية' : 'Plan gratuit'}
                            </p>
                            <p className="text-sm text-slate-600">{t.freeAfterTrial}</p>
                        </div>
                    </div>

                    {/* Premium */}
                    <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
                        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-500 text-white">
                            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                        </div>
                        <div>
                            <p className="font-semibold text-amber-900">Premium</p>
                            <p className="text-sm text-amber-700">{t.premiumExplanation}</p>
                        </div>
                    </div>
                </div>

                {/* CTA button */}
                <button
                    onClick={handleStart}
                    disabled={loading}
                    className="w-full rounded-xl bg-blue-600 py-3 px-4 font-bold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-50"
                >
                    {loading ? (lang === 'ar' ? 'جاري البدء...' : 'Activation...') : t.startTrial}
                </button>
            </div>
        </div>
    )
}
